import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	getLoggedInChatUser,
	initChat,
	isChatSignedOut,
	loginToChat,
	logoutOfChat,
} from "./cometchat/chat_client";
import axios from "./axios_config";
import { BASE_API_URL } from "./common/endpoints";
import { useUserDataContext } from "./user_data_context";
import { useMediaAutoplayGuard } from "./hooks/use_media_autoplay_guard";
import { describeError } from "./common/utils/error_message";

export const COMETCHAT_CONSTANTS = {
	APP_ID: import.meta.env.VITE_COMETCHAT_APP_ID,
	REGION: import.meta.env.VITE_COMETCHAT_REGION,
	get API_BASE() {
		return `https://${this.APP_ID}.apiclient-${this.REGION}.cometchat.io/v3.0`;
	},
};

const AuthContext = createContext(null);

// ------------------------ helpers ------------------------

async function safeLogout() {
	try {
		await logoutOfChat();
	} catch {
		// ignore
	}
}

async function getExistingCometChatUser() {
	try {
		return await getLoggedInChatUser();
	} catch {
		return null;
	}
}

async function loginWithToken(authToken) {
	return await loginToChat(authToken);
}

async function fetchChatToken() {
	const { data } = await axios.post(`${BASE_API_URL}/v1/users/comet-chat/auth-token`);
	const authToken = data?.authToken;
	if (!authToken || typeof authToken !== "string") {
		throw new Error("Invalid authToken received from backend");
	}
	return authToken;
}

function isAuthTokenNotFoundError(err) {
	const code =
		err?.code || err?.error?.code || err?.error?.error?.code || err?.response?.data?.error?.code;

	const msg = err?.message || err?.error?.message || err?.response?.data?.error?.message || "";

	return (
		code === "AUTH_ERR_AUTH_TOKEN_NOT_FOUND" ||
		code === "ERR_AUTH_TOKEN_NOT_FOUND" ||
		/AUTH_ERR_AUTH_TOKEN_NOT_FOUND/i.test(String(msg)) ||
		/auth token .* does not exist/i.test(String(msg))
	);
}

// Important: do NOT parse "401" from message (false positives from other libs)
function isUnauthorized(err) {
	const status =
		err?.status || err?.response?.status || err?.error?.status || err?.response?.data?.status;

	return status === 401;
}

// ------------------------ provider ------------------------

export function CometChatAuthProvider({ children }) {
	const { userData, isAuthChecked } = useUserDataContext();

	const [isSdkReady, setIsSdkReady] = useState(false);
	const [loggedInUser, setLoggedInUser] = useState(null);
	const [isAuthLoading, setIsAuthLoading] = useState(false);
	// A failed init or login used to leave the chat page on an endless spinner or on a
	// false "register" message. These let it say so and try again.
	const [initError, setInitError] = useState(false);
	const [loginError, setLoginError] = useState(false);
	const [initAttempt, setInitAttempt] = useState(0);
	const [loginAttempt, setLoginAttempt] = useState(0);

	useMediaAutoplayGuard();

	const siteUserId = userData?._id;
	const userCometChatEnabled = !!userData?.user_cometchat;

	// The SDK starts at once if this browser may hold a chat session (to reuse or clean up),
	// otherwise only for a signed-in account that has chat.
	const [startedSignedOut] = useState(isChatSignedOut);
	const chatNeeded = !startedSignedOut || (isAuthChecked && !!siteUserId && userCometChatEnabled);
	// Consumers read this as "the chat state is settled": ready, or known to be unneeded.
	const isUiKitInitialized = isSdkReady || (isAuthChecked && !chatNeeded);

	const isMountedRef = useRef(true);
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const setLoggedInUserSafe = u => {
		if (isMountedRef.current) setLoggedInUser(u);
	};

	// --- relogin: mutex + cooldown
	const reloginPromiseRef = useRef(null);
	const lastReloginAtRef = useRef(0);
	const COOLDOWN_MS = 15_000;

	const reloginOnce = async () => {
		// If relogin is already running, reuse the same promise
		if (reloginPromiseRef.current) return reloginPromiseRef.current;

		const now = Date.now();
		if (now - lastReloginAtRef.current < COOLDOWN_MS) {
			// Cooldown active: don't start a new attempt and don't "fake success"
			return null;
		}

		lastReloginAtRef.current = now;

		reloginPromiseRef.current = (async () => {
			// Guard: if user logged out or feature disabled - ensure CC logged out too
			if (!siteUserId || !userCometChatEnabled) {
				await safeLogout();
				setLoggedInUserSafe(null);
				return null;
			}

			try {
				// Soft attempt: token + login without logout (less disruptive)
				const t1 = await fetchChatToken();
				const u1 = await loginWithToken(t1);
				setLoggedInUserSafe(u1);
				return u1;
			} catch (e1) {
				try {
					// Hard attempt: clear CC internal state and retry once
					await safeLogout();
					const t2 = await fetchChatToken();
					const u2 = await loginWithToken(t2);
					setLoggedInUserSafe(u2);
					return u2;
				} catch (e2) {
					await safeLogout();
					setLoggedInUserSafe(null);
					console.error("CometChat relogin failed:", describeError(e2));
					return null;
				}
			} finally {
				reloginPromiseRef.current = null;
			}
		})();

		return reloginPromiseRef.current;
	};

	// Initialize the chat SDK once (again only on a retry after a failure). The UI kit is
	// loaded by the /chats page itself; isUiKitInitialized keeps its name for the consumers.
	useEffect(() => {
		if (!chatNeeded) return undefined;
		let cancelled = false;
		setInitError(false);

		initChat()
			.then(() => {
				if (!cancelled) setIsSdkReady(true);
			})
			.catch(err => {
				console.error("CometChat init failed:", describeError(err));
				if (!cancelled) setInitError(true);
			});

		return () => {
			cancelled = true;
		};
	}, [initAttempt, chatNeeded]);

	// Main auth bootstrap
	useEffect(() => {
		if (!isSdkReady || !isAuthChecked) return;

		let cancelled = false;

		(async () => {
			if (!cancelled) {
				setIsAuthLoading(true);
				setLoginError(false);
			}

			try {
				if (!siteUserId) {
					await safeLogout();
					if (!cancelled) setLoggedInUser(null);
					return;
				}

				if (!userCometChatEnabled) {
					await safeLogout();
					if (!cancelled) setLoggedInUser(null);
					return;
				}

				const existingUser = await getExistingCometChatUser();
				const existingUid = existingUser?.getUid?.() ?? existingUser?.uid;

				if (existingUser && existingUid === siteUserId) {
					if (!cancelled) setLoggedInUser(existingUser);
					return;
				}

				if (existingUser && existingUid && existingUid !== siteUserId) {
					await safeLogout();
				}

				const authToken = await fetchChatToken();
				const user = await loginWithToken(authToken);
				if (!cancelled) setLoggedInUser(user);
			} catch (err) {
				if (
					siteUserId &&
					userCometChatEnabled &&
					(isAuthTokenNotFoundError(err) || isUnauthorized(err))
				) {
					const u = await reloginOnce();
					if (u) return;
					// if null (cooldown or failed) -> fall through to fallback
				}

				await safeLogout();
				if (!cancelled) setLoggedInUser(null);

				const status = err?.response?.status;
				if (status === 403) console.warn("CometChat: forbidden (403)");
				else if (status === 404) console.warn("CometChat: user not found (404)");
				else {
					// 403/404 mean the account has no chat; anything else is worth another try.
					console.error("CometChat login failed:", describeError(err));
					if (!cancelled) setLoginError(true);
				}
			} finally {
				if (!cancelled) setIsAuthLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [isSdkReady, isAuthChecked, siteUserId, userCometChatEnabled, loginAttempt]);

	// Catch auth failures after idle (optional best-effort)
	useEffect(() => {
		if (!isSdkReady || !isAuthChecked) return;

		const looksLikeCometChatAuthError = err => {
			const code =
				err?.code ||
				err?.error?.code ||
				err?.error?.error?.code ||
				err?.response?.data?.error?.code;

			return (
				typeof code === "string" &&
				(code.startsWith("AUTH_") || code.includes("AUTH_TOKEN") || code.includes("ERR_AUTH"))
			);
		};

		const onUnhandledRejection = event => {
			const err = event?.reason;
			if (!err) return;

			if (
				siteUserId &&
				userCometChatEnabled &&
				looksLikeCometChatAuthError(err) &&
				(isAuthTokenNotFoundError(err) || isUnauthorized(err))
			) {
				// best-effort; cooldown/mutex inside
				reloginOnce().catch(() => {});
			}
		};

		window.addEventListener("unhandledrejection", onUnhandledRejection);
		return () => {
			window.removeEventListener("unhandledrejection", onUnhandledRejection);
		};
	}, [isSdkReady, isAuthChecked, siteUserId, userCometChatEnabled]);

	const hasConnectionError = initError || loginError;
	const retryConnection = useCallback(() => {
		if (initError) setInitAttempt(n => n + 1);
		else setLoginAttempt(n => n + 1);
	}, [initError]);

	const value = useMemo(
		() => ({
			isUiKitInitialized,
			loggedInUser,
			isAuthLoading,
			hasConnectionError,
			retryConnection,
			reloginOnce, // optional: to be used by your own CC call wrappers
		}),
		[isUiKitInitialized, loggedInUser, isAuthLoading, hasConnectionError, retryConnection],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useCometChatAuth() {
	const ctx = useContext(AuthContext);
	if (!ctx) {
		throw new Error("useCometChatAuth must be used inside CometChatAuthProvider");
	}
	return ctx;
}
